import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import { API_config } from '../../config/apiConfig';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'bulma/css/bulma.min.css';
import '../../styles/App.css';
import CompactSidePanel from '../../components/dashboard/CompactSidePanel';
import GPXLoaderSQL from '../../services/GPXLoaderSQL';
import MarkerClusterComponent from '../../components/map/MarkerClusterComponent';
import DeviceInfoPopup from '../../components/map/DeviceInfoPopup';
import DeviceStatusModal from '../../components/map/DeviceStatusModal';
import TraccarApiPoller from '../../services/TraccarApiPoller';
import SpecialMarkers from '../../components/map/SpecialMarkers';
import TopNotification from '../../components/common/TopNotification';
import Watermark from '../../components/common/Watermark';
import SafetyPointsLayer from '../../components/map/SafetyPointsLayer';
import { computeDeviceDistanceOnOdcinki } from '../../utils/stageUtils';
import HazardMarkers from '../../components/map/HazardMarkers';
import ImageOverlayLayer from '../../components/map/ImageOverlayLayer';
import { useAlertFlag } from '../../hooks/useAlertFlag';
import 'leaflet-geometryutil';
import { useAlertEngine } from '../../hooks/useAlertEngine';

import { SOS_ALERT_DEF } from '../../constants/alertDefinitions';
import { MAP_LAYERS } from '../../constants/mapLayers';
import AreaSelectionLayer from '../../components/map/AreaSelectionLayer';
import AreaSelectionPopup from '../../components/map/AreaSelectionPopup';

const getCarNumberFromDevice = (device = {}) => {
    const rawName = (device.deviceName || device.name || '').trim();
    if (rawName) {
        const [firstPart] = rawName.split('-');
        const trimmed = (firstPart || '').trim();
        if (trimmed) {
            return trimmed;
        }
        return rawName;
    }

    const fallbackId = device.deviceId ?? device.id;
    if (fallbackId != null) {
        return String(fallbackId);
    }

    return 'Unknown';
};


function LoginScreen({ onLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_config.apiUrl}/session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ email, password }),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Invalid credentials');
            const userData = await res.json();
            // Traccar readonly users have administrator === false
            const isAdmin = userData?.administrator === true;
            localStorage.setItem('mainAuth', 'true');
            localStorage.setItem('mainAuthIsAdmin', String(isAdmin));
            onLogin();
        } catch {
            setError('Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a2e' }}>
            <div className="box" style={{ width: '360px' }}>
                <h1 className="title has-text-centered" style={{ fontSize: '1.4rem' }}>MonitGPS Login</h1>
                {error && <div className="notification is-danger is-light py-2">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="field">
                        <label className="label" style={{ color: '#363636' }}>Email</label>
                        <div className="control">
                            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email" required autoFocus style={{ background: '#fff', color: '#363636' }} />
                        </div>
                    </div>
                    <div className="field">
                        <label className="label" style={{ color: '#363636' }}>Password</label>
                        <div className="control">
                            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ background: '#fff', color: '#363636' }} />
                        </div>
                    </div>
                    <div className="field">
                        <button className={`button is-primary is-fullwidth${loading ? ' is-loading' : ''}`} type="submit">Login</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function MonitoringDashboard() {
    const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('mainAuth') === 'true');
    const [isReadOnly, setIsReadOnly] = useState(() => localStorage.getItem('mainAuthIsAdmin') !== 'true');
    const [devicePositions, setDevicePositions] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [config, setConfig] = useState(null);
    const [deviceModels, setDeviceModels] = useState([]);
    const [modelIcons, setModelIcons] = useState({});
    const [modelColors, setModelColors] = useState({});
    const [modelIsFunctional, setModelIsFunctional] = useState({});
    const [modelShowInProgressBar, setModelShowInProgressBar] = useState({});
    const [odcinkiData, setOdcinkiData] = useState([]);
    const mapRef = useRef();
    const zoom = 15;
    const [enrichedPositions, setEnrichedPositions] = useState([]);
    // const [alerts, setAlerts] = useState([]); // REMOVED: Managed by useAlertEngine
    const [sections, setSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedStageId, setSelectedStageId] = useState(null);
    const [selectedStageName, setSelectedStageName] = useState('');
    const [currentMap, setCurrentMap] = useState(MAP_LAYERS[0]);
    const [rainViewerData, setRainViewerData] = useState({ host: null, path: null, ts: null });
    const [showSafetyPoints, setShowSafetyPoints] = useState(false);
    const [showImageOverlay, setShowImageOverlay] = useState(false);
    const [statusModalDevice, setStatusModalDevice] = useState(null);
    const [areaSelectionMode, setAreaSelectionMode] = useState(false);
    const [areaSelectedDevices, setAreaSelectedDevices] = useState(null);

    useEffect(() => {
        if (!selectedSection && sections.length > 0) {
            const defaultSection = sections[0];
            if (defaultSection?.section) {
                setSelectedSection(defaultSection.section);
            }

            if (
                !selectedStageId &&
                Array.isArray(defaultSection?.stages) &&
                defaultSection.stages.length > 0
            ) {
                const defaultStage = defaultSection.stages[0];
                if (defaultStage?.id != null) {
                    setSelectedStageId(defaultStage.id);
                }
                setSelectedStageName(defaultStage?.name ?? '');
            }
        }
    }, [sections, selectedSection, selectedStageId]);

    useEffect(() => {
        if (currentMap?.type === 'rainviewer') {
            const fetchRainTs = async () => {
                try {
                    console.log('Fetching RainViewer timestamp...');
                    const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
                    if (res.ok) {
                        const data = await res.json();
                        console.log('RainViewer Data:', data);
                        if (data?.radar?.past?.length > 0) {
                            const last = data.radar.past[data.radar.past.length - 1];
                            console.log('Selected Timestamp:', last.time);
                            console.log('Host + Path:', data.host + last.path);
                            setRainViewerData({
                                host: data.host, // https://tilecache.rainviewer.com
                                path: last.path, // /v2/radar/1771448400
                                ts: last.time
                            });
                        } else {
                            console.warn('No past radar data found.');
                        }
                    } else {
                        console.error('RainViewer API error:', res.status);
                    }
                } catch (e) {
                    console.error('Failed to fetch RainViewer TS', e);
                }
            };
            fetchRainTs();
        }
    }, [currentMap]);

    const deviceStateRef = useRef(new Map()); // deviceId -> { stoppedSince, sos, lastOnStage, lastRouteId, lastOffset, movingStreak, alertShown }
    const deviceLastSeenRef = useRef(new Map()); // deviceId -> { serverTime, clientTime } — client-side staleness tracking
    const [nowTick, setNowTick] = useState(Date.now()); // co 1 s odświeża countdown

    const { isActive: isHazardDisplayActive } = useAlertFlag();

    // --- ALERTS ENGINE INTEGRATION ---
    // Feed ALL enriched positions so SOS/OK alerts fire for every device.
    // STOP_ALERT_DEF is safe because stoppedSince is forced null for functional devices (line ~369),
    // so it will never trigger for service/official vehicles.
    const { activeAlerts, ackAlert, isAcked } = useAlertEngine(enrichedPositions);

    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current;

        // progi
        const STOP_KMH = 2;
        const MOVE_CLEAR_KMH = 5;
        const CLEAR_STREAK = 4;        // ile kolejnych próbek ruchu kasuje SOS (poll co 2s -> ~8s)
        const SOS_AFTER_MS = 10000;    // 10 s postoju
        const ENTER_OFFSET = 40;
        const EXIT_OFFSET = 20;          // widened from 8m to absorb GPS noise
        const OFF_STAGE_STREAK = 3;      // consecutive off-stage polls before leaving (poll=1s → ~3s)
        const SWITCH_GAIN = 12;
        const BEFORE_STAGE_TOLERANCE_M = 35; // ile metrów za linią startu uznajemy jeszcze za "przed OS"
        const EXIT_FINISH_THRESHOLD = 0.90;  // ≥90% of stage = finished normally, <90% = off-track exit

        const states = deviceStateRef.current;

        // Update client-side "last seen" timestamps — only advances when serverTime actually changes,
        // so we compare two client clocks instead of client vs server clock.
        const clientNow = Date.now();
        devicePositions.forEach(d => {
            const id = d.deviceId || d.id;
            const prev = deviceLastSeenRef.current.get(id);
            if (!prev || prev.serverTime !== d.serverTime) {
                deviceLastSeenRef.current.set(id, { serverTime: d.serverTime, clientTime: clientNow });
            }
        });

        // pomocniczo: projekcja na podany odcinek (zwraca offset i dystans od startu)
        const projectOnRoute = (latlng, routeIndex) => {
            if (!routeIndex) return null;
            const { latlngs, cum } = routeIndex;
            if (!latlngs || latlngs.length < 2) return null;

            let best = { minD: Infinity, snapped: null, segIdx: -1 };
            for (let i = 1; i < latlngs.length; i++) {
                const A = latlngs[i - 1];
                const B = latlngs[i];
                const snapped = L.GeometryUtil.closestOnSegment(map, latlng, A, B);
                if (!snapped) continue;

                // Use geodesic distance (meters) between the device and the snapped point.
                // distanceSegment() returns planar pixel distance, which depends on zoom level
                // and caused SOS triggers to vary with map zoom.
                const d = latlng.distanceTo(snapped);
                if (d < best.minD) {
                    best.minD = d;
                    best.snapped = snapped;
                    best.segIdx = i;
                }
            }
            if (!best.snapped) return null;
            const i = best.segIdx;
            const distanceFromStart = cum[i - 1] + latlngs[i - 1].distanceTo(best.snapped);
            return {
                offset: best.minD,
                distance_m: distanceFromStart,
                segIdx: best.segIdx,
                snapped: best.snapped,
            };
        };

        const isBeforeStageStart = (route, proj, deviceLatLng) => {
            if (!route?.routeIndex?.latlngs || route.routeIndex.latlngs.length < 2 || !proj) {
                return false;
            }
            if (proj.segIdx > 1) return false;
            const start = route.routeIndex.latlngs[0];
            const next = route.routeIndex.latlngs[1];
            if (!start || !next) return false;

            const startPoint = map.project(start);
            const nextPoint = map.project(next);
            const devicePoint = map.project(deviceLatLng);

            const stageVec = {
                x: nextPoint.x - startPoint.x,
                y: nextPoint.y - startPoint.y,
            };
            const stageVecLenSq = stageVec.x * stageVec.x + stageVec.y * stageVec.y;
            if (stageVecLenSq === 0) return false;

            const deviceVec = {
                x: devicePoint.x - startPoint.x,
                y: devicePoint.y - startPoint.y,
            };
            const dot = stageVec.x * deviceVec.x + stageVec.y * deviceVec.y;
            if (dot >= 0) return false;

            return deviceLatLng.distanceTo(start) > BEFORE_STAGE_TOLERANCE_M;
        };
        // const alertsToAdd = []; REMOVED
        const next = devicePositions.map((d) => {
            // Check if this device model is functional (isFunctional: false disables SOS)
            const deviceModelName = d.deviceModel || d.model;
            const isDeviceFunctional = modelIsFunctional[deviceModelName] !== false;
            const id = d.deviceId ?? d.id;
            const prev = states.get(id) || {
                stoppedSince: null,
                sos: false,
                lastOnStage: false,
                lastRouteId: null,    // <— zapamiętana trasa
                lastOffset: Infinity, // <— ostatni offset na tej trasie
                movingStreak: 0,
                offStageStreak: 0,   // debounce: must be off-stage N polls before leaving
                alertShown: false,
                hasMovedOnStage: false,    // must move on stage before stopped-timer starts
                lastDistanceOnStage_m: 0,  // last known distance from stage start (meters)
                totalStageLength_m: 0,     // total length of the current stage (meters)
                exitedMidStage: false,     // true when car left the stage before finish line
                offStageSince: null,       // timestamp when car went off-stage mid-run (for 10s countdown)
            };
            const deviceLL = L.latLng(d.latitude, d.longitude);
            const speedKmh = (d.speed || 0) * 1.852;
            const hasMotionAttr = d.attributes && typeof d.attributes.motion === 'boolean';
            // Speed is authoritative: if GPS shows >MOVE_CLEAR_KMH the car is moving,
            // regardless of the motion attribute (which can be stale from device-level attrs).
            const isStopped = speedKmh > MOVE_CLEAR_KMH
                ? false
                : (hasMotionAttr ? (d.attributes.motion === false) : (speedKmh <= STOP_KMH));

            // 1) policz projekcję na WSZYSTKIE trasy i wybierz najlepszą
            let best = { idx: -1, route: null, proj: null };
            odcinkiData.forEach((o, idx) => {
                if (!o.routeIndex) return;
                const proj = projectOnRoute(deviceLL, o.routeIndex);
                if (!proj) return;
                if (!best.proj || proj.offset < best.proj.offset) {
                    best = { idx, route: o, proj };
                }
            });

            // 2) histereza wyboru trasy (STICKY)
            let chosenIdx = best.idx;
            let chosen = best; // {idx, route, proj}
            if (prev.lastRouteId != null && odcinkiData[prev.lastRouteId]?.routeIndex) {
                const prevRoute = odcinkiData[prev.lastRouteId];
                const prevProj = projectOnRoute(deviceLL, prevRoute.routeIndex);
                if (prevProj) {
                    if (prev.lastOnStage) {
                        // On stage: always stay on the assigned road — never switch to another polyline.
                        // A car on a 2-road stage must not silently jump to the parallel road when it exits.
                        chosenIdx = prev.lastRouteId;
                        chosen = { idx: prev.lastRouteId, route: prevRoute, proj: prevProj };
                    } else {
                        // Off stage: allow switching to a better route (normal hysteresis)
                        const gain = best.proj ? (prevProj.offset - best.proj.offset) : -Infinity;
                        const keepPrev =
                            (prevProj.offset <= EXIT_OFFSET) ||
                            (gain < SWITCH_GAIN);
                        if (keepPrev) {
                            chosenIdx = prev.lastRouteId;
                            chosen = { idx: prev.lastRouteId, route: prevRoute, proj: prevProj };
                        }
                    }
                }
            }

            // 3) histereza wejście/wyjście z OS
            const beforeStage = isBeforeStageStart(chosen.route, chosen.proj, deviceLL);

            let offStageStreak = prev.offStageStreak || 0;
            let onStage;
            if (prev.lastOnStage) {
                const rawOffStage = !(chosen.proj && chosen.proj.offset <= EXIT_OFFSET);
                if (rawOffStage) {
                    offStageStreak += 1;
                    // Stay on stage until streak threshold is reached (debounce jitter)
                    onStage = offStageStreak < OFF_STAGE_STREAK;
                } else {
                    offStageStreak = 0;   // back on track — reset streak
                    onStage = true;
                }
            } else {
                offStageStreak = 0;
                onStage = chosen.proj ? (chosen.proj.offset <= ENTER_OFFSET && !beforeStage) : false;
            }

            // 4) track distance on stage and detect mid-stage exit (with 10s countdown)
            let { lastDistanceOnStage_m, totalStageLength_m, exitedMidStage, offStageSince } = prev;

            if (onStage && chosen.proj) {
                lastDistanceOnStage_m = chosen.proj.distance_m;
                const cum = chosen.route?.routeIndex?.cum;
                if (cum?.length) totalStageLength_m = cum[cum.length - 1];
            }

            if (onStage) {
                // Back on stage — reset everything
                exitedMidStage = false;
                offStageSince = null;
            } else if (prev.lastOnStage && prev.hasMovedOnStage) {
                // Transition frame: car just went off-stage after moving on it.
                // Only start the countdown if the exit looks like an off-track exit, not a finish.
                const ratio = totalStageLength_m > 0 ? lastDistanceOnStage_m / totalStageLength_m : 0;
                if (ratio < EXIT_FINISH_THRESHOLD) {
                    offStageSince = offStageSince || Date.now();
                }
            } else if (!onStage && offStageSince && !exitedMidStage) {
                // Counting down — confirm exit after 10 s of being off-stage
                if (Date.now() - offStageSince >= 10000) {
                    exitedMidStage = true;
                }
            }

            // 5) logika SOS z latchingiem
            let { stoppedSince, sos, movingStreak, hasMovedOnStage } = prev;
            // Non-functional devices can never be in SOS — clear any stale latch
            if (!isDeviceFunctional) sos = false;

            // >>> LOGIKA SOS – ZATRZASK <<<
            if (onStage) {
                if (sos) {
                    // kasuj SOS tylko po CIĄGŁYM ruchu
                    if (speedKmh >= MOVE_CLEAR_KMH) {
                        movingStreak = (prev.movingStreak || 0) + 1;
                        if (movingStreak >= CLEAR_STREAK) {
                            sos = false;
                            stoppedSince = null;
                            movingStreak = 0;
                        }
                    } else {
                        movingStreak = 0;
                    }
                } else {
                    // sprawdzaj wejście do SOS tylko na OS
                    // Use speed to detect genuine movement — independent of the motion attribute
                    // which can lag behind (still false while the car is already accelerating).
                    if (speedKmh >= MOVE_CLEAR_KMH) hasMovedOnStage = true;

                    if (isStopped) {
                        // Only start counting after the car has moved on-stage at least once.
                        // This prevents a false countdown when the car enters the stage stopped
                        // (e.g. waiting at the start line or GPS showing 0 on entry).
                        if (hasMovedOnStage) {
                            if (!stoppedSince) stoppedSince = Date.now();
                            // SOS blink only for non-functional devices (functional cars stopping is expected)
                            if (Date.now() - stoppedSince >= SOS_AFTER_MS && !isDeviceFunctional) {
                                sos = true;            // LATCH: od teraz SOS trzyma się twardo
                                movingStreak = 0;
                            }
                        }
                    } else {
                        stoppedSince = null;
                        movingStreak = (speedKmh >= MOVE_CLEAR_KMH) ? (prev.movingStreak || 0) + 1 : 0;
                    }
                }
            } else {
                // poza OS nie analizujemy SOS
                sos = false;
                stoppedSince = null;
                movingStreak = 0;
                hasMovedOnStage = false;
            }

            // REMOVED: Alert generation logic here. Now handled by useAlertEngine.

            // zapisz stan
            states.set(id, {
                stoppedSince,
                sos,
                lastOnStage: onStage,
                lastRouteId: onStage ? chosenIdx : null,
                lastOffset: onStage ? (chosen.proj?.offset ?? Infinity) : Infinity,
                movingStreak,
                offStageStreak,
                alertShown: prev.alertShown,
                hasMovedOnStage: onStage ? hasMovedOnStage : false,
                lastDistanceOnStage_m,
                totalStageLength_m,
                exitedMidStage,
                offStageSince,
            });

            // Countdown 10→0 while stopped on stage, before SOS triggers.
            // Null once sos=true (warning shown instead) or when not stopped.
            const countdownSec = (onStage && isStopped && !sos && stoppedSince && !isDeviceFunctional)
                ? Math.max(0, 10 - Math.floor((Date.now() - stoppedSince) / 1000))
                : null;

            return {
                ...d,
                sos,
                isStopped,
                countdownSec,
                onStage,
                // Functional devices never expose stoppedSince → only non-functional trigger STOP_ALERT_DEF
                stoppedSince: !isDeviceFunctional ? stoppedSince : null,
                routeName: onStage ? (chosen.route?.name ?? null) : null,
                distanceOnPolyline: onStage && chosen.proj
                    ? (chosen.proj.distance_m / 1000).toFixed(2)
                    : 'Off-Track',
                exitedMidStage,
                lastDistanceOnStage_m,
                offTrackCountdownSec: (offStageSince && !exitedMidStage)
                    ? Math.max(0, 10 - Math.floor((Date.now() - offStageSince) / 1000))
                    : null,
            };
        });

        const finalPositions = next.map(d => {
            const devId = d.deviceId || d.id;
            const devModelName = d.deviceModel || d.model;
            const devIsFunctional = modelIsFunctional[devModelName] !== false;

            const hasActiveSos = !devIsFunctional && activeAlerts.some(a =>
                String(a.device.deviceId) === String(devId) && a.definitionId === SOS_ALERT_DEF.id
            );

            const isSosActive = hasActiveSos || d.isSosActive === true;

            let forcedSos = d.sos;
            if (isSosActive) {
                forcedSos = true;
            }

            const lastSeen = deviceLastSeenRef.current.get(d.deviceId || d.id);
            const isStale = lastSeen ? (clientNow - lastSeen.clientTime) > 3000 : false;

            return {
                ...d,
                sos: forcedSos,
                isSosActive,
                isStale,
            };
        });

        setEnrichedPositions(finalPositions);
        // if (alertsToAdd.length > 0) { setAlerts... } REMOVED

        // 5) aktualizuj otwarty popup TYLKO przy istotnych zmianach (bez countdownu)
        if (selectedDevice) {
            const upd = finalPositions.find(x => (x.deviceId ?? x.id) === selectedDevice.deviceId);
            if (upd) {
                const importantChanged =
                    upd.latitude !== selectedDevice.latitude ||
                    upd.longitude !== selectedDevice.longitude ||
                    upd.sos !== selectedDevice.sos ||
                    upd.onStage !== selectedDevice.onStage ||
                    upd.distanceOnPolyline !== selectedDevice.distanceOnPolyline ||
                    upd.routeName !== selectedDevice.routeName ||
                    upd.isStale !== selectedDevice.isStale;

                if (importantChanged) {
                    setSelectedDevice(upd);
                } else {
                    // merge lightweight updates like speed or countdown without re-rendering the whole popup
                    const partial = {};
                    if (upd.speed !== selectedDevice.speed) partial.speed = upd.speed;
                    if (upd.countdownSec !== selectedDevice.countdownSec) {
                        partial.countdownSec = upd.countdownSec;
                    }
                    if (Object.keys(partial).length > 0) {
                        setSelectedDevice(prev => ({ ...prev, ...partial }));
                    }
                }
            }
        }
        // 6) Sync Status Modal device if open (to keep it stable even if list flickers)
        if (statusModalDevice) {
            const fresh = finalPositions.find(x => (x.deviceId ?? x.id) === (statusModalDevice.deviceId ?? statusModalDevice.id));
            if (fresh) {
                // Only update if something changed to minimize re-renders
                const important = fresh.latitude !== statusModalDevice.latitude || 
                                  fresh.sos !== statusModalDevice.sos ||
                                  fresh.isSosActive !== statusModalDevice.isSosActive;
                if (important) setStatusModalDevice(fresh);
            }
        }
    }, [devicePositions, odcinkiData, nowTick, activeAlerts, modelIsFunctional, selectedDevice, statusModalDevice]);

    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    // Fetch config from public/config.json
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const response = await fetch(`${process.env.PUBLIC_URL || ''}/config.json`);
                if (!response.ok) throw new Error('Failed to load configuration');
                const data = await response.json();
                setConfig(data);
            } catch (error) {
                console.error('Error loading config:', error);
            }
        };

        loadConfig();
    }, []);

    // Load device models once configuration is available
    useEffect(() => {
        if (!config?.deviceModelsUrl) return;
        const loadModels = async () => {
            try {
                const response = await fetch(`${process.env.PUBLIC_URL || ''}/${config.deviceModelsUrl}`);
                if (!response.ok) throw new Error('Failed to load device models');
                const data = await response.json();
                const modelsArrayRaw = Array.isArray(data) ? data : data?.models;
                const modelsArray = Array.isArray(modelsArrayRaw) ? modelsArrayRaw : [];
                const icons = {};
                const colors = {};
                const isFunctional = {};
                const showInProgressBar = {};
                const names = [];

                modelsArray.forEach(m => {
                    if (typeof m === 'string') {
                        if (m !== 'Warning') names.push(m);
                        return;
                    }

                    if (m && typeof m === 'object' && m.name !== undefined) {
                        const { name } = m;
                        if (name !== 'Warning') names.push(name);
                        icons[name] = m.icon || '';
                        if (m.color) colors[name] = m.color;
                        if (m.isFunctional !== undefined) isFunctional[name] = m.isFunctional;
                        if (m.showInProgressBar !== undefined) showInProgressBar[name] = m.showInProgressBar;
                    }
                });

                setDeviceModels(names);
                setModelIcons(icons);
                setModelColors(colors);
                setModelIsFunctional(isFunctional);
                setModelShowInProgressBar(showInProgressBar);

                // Clear any stale latched alerts for non-functional models from localStorage
                try {
                    const LATCH_KEY = 'tracking_app_latched_alerts';
                    const stored = localStorage.getItem(LATCH_KEY);
                    if (stored) {
                        const latches = JSON.parse(stored);
                        // We don't know device IDs yet, but we can't filter by model here.
                        // Just clear the entire latch map on model reload to remove stale entries.
                        localStorage.removeItem(LATCH_KEY);
                    }
                } catch (e) { /* ignore */ }
            } catch (err) {
                console.error('Error loading device models:', err);
            }
        };
        loadModels();
    }, [config]);

    const handleDeviceClick = (latitude, longitude, deviceId) => {
        console.log("📌 handleDeviceClick called with ID:", deviceId);

        if (mapRef.current) {
            mapRef.current.setView([latitude, longitude], 15);
        }

        // Use enriched data when available so status/Stage stay consistent
        const enriched = enrichedPositions.find(d => d.deviceId === deviceId);
        if (enriched) {
            setSelectedDevice(enriched);
            return;
        }

        // Fallback: compute distance for raw device info
        const device = devicePositions.find(d => d.deviceId === deviceId);
        if (device) {
            const distanceOnPolyline = computeDeviceDistanceOnOdcinki(
                mapRef.current,
                odcinkiData,
                device
            );
            setSelectedDevice({ ...device, distanceOnPolyline });
        } else {
            console.error("❌ Device Not Found in devicePositions:", devicePositions);
        }
    };

    const handleModelUpdate = (deviceId, model) => {
        setEnrichedPositions(prev =>
            prev.map(d =>
                d.deviceId === deviceId
                    ? { ...d, model, deviceModel: model }
                    : d
            )
        );
        setSelectedDevice(prev =>
            prev && prev.deviceId === deviceId
                ? { ...prev, model, deviceModel: model }
                : prev
        );
    };
    const handleAlertClose = (id) => {
        // setAlerts((prev) => prev.filter((a) => a.id !== id));
        // Managed by engine ack/clear
    };

    const handleAlertAction = (alert) => {
        const { device } = alert;
        handleDeviceClick(device.latitude, device.longitude, device.deviceId);
        // handleAlertClose(alert.id);
    };

    const handleConfirmSos = (deviceId) => {
        ackAlert(deviceId, SOS_ALERT_DEF.id);
    };

    const handleAreaSelected = (bounds) => {
        const devicesInArea = enrichedPositions.filter(device =>
            typeof device.latitude === 'number' &&
            typeof device.longitude === 'number' &&
            bounds.contains([device.latitude, device.longitude])
        );
        setAreaSelectedDevices(devicesInArea);
        setAreaSelectionMode(false);
    };

    const handleOdcinekClick = (bounds) => {
        if (mapRef.current && bounds) {
            const center = bounds.getCenter();
            mapRef.current.setView([center.lat, center.lng], zoom);
        }
    };

    if (!isLoggedIn) {
        return <LoginScreen onLogin={() => {
            setIsLoggedIn(true);
            setIsReadOnly(localStorage.getItem('mainAuthIsAdmin') !== 'true');
        }} />;
    }

    return (
        <div className="App" style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
            <div style={{ position: 'fixed', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
                {activeAlerts.map((alert) => (
                    <TopNotification
                        key={alert.id}
                        message={alert.message}
                        type={alert.severity}
                        duration={alert.definitionId === 'SOS' || alert.definitionId === 'OFF_TRACK_EXIT' ? null : alert.definitionId === 'OK' ? 30000 : 5000}
                        onClose={() => ackAlert(alert.device.deviceId, alert.definitionId)}
                        onAction={() => handleAlertAction(alert)}
                        actionLabel="Show"
                    />
                ))}
            </div>

            <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button
                    className={`button is-small ${showSafetyPoints ? 'is-warning' : 'is-info'}`}
                    style={{
                        boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setShowSafetyPoints(!showSafetyPoints)}
                    title={showSafetyPoints ? "Hide Safety Points" : "Show Safety Points"}
                >
                    <span className="icon">
                        <span className="material-icons-outlined" style={{fontSize: '18px', color: showSafetyPoints ? '#000' : '#fff'}}>
                            security
                        </span>
                    </span>
                </button>
                <button
                    className={`button is-small ${showImageOverlay ? 'is-warning' : 'is-info'}`}
                    style={{
                        boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => {
                        const next = !showImageOverlay;
                        setShowImageOverlay(next);
                        if (next) setShowSafetyPoints(true);
                    }}
                    title={showImageOverlay ? "Hide map overlay" : "Show map overlay"}
                >
                    <span className="icon">
                        <span className="material-icons-outlined" style={{fontSize: '18px', color: showImageOverlay ? '#000' : '#fff'}}>
                            map
                        </span>
                    </span>
                </button>
                <button
                    className={`button is-small ${areaSelectionMode ? 'is-warning' : 'is-info'}`}
                    style={{
                        boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => {
                        setAreaSelectionMode(prev => !prev);
                        setAreaSelectedDevices(null);
                    }}
                    title={areaSelectionMode ? "Cancel area selection" : "Select area to count cars"}
                >
                    <span className="icon">
                        <span className="material-icons-outlined" style={{fontSize: '18px', color: areaSelectionMode ? '#000' : '#fff'}}>
                            select_all
                        </span>
                    </span>
                </button>
            </div>

            {/* WebSocket connection
      <TraccarWebSocket onPositionsUpdate={setDevicePositions} />*/}
            {/* REST polling (replaces WebSocket) */}
            <TraccarApiPoller onPositionsUpdate={setDevicePositions} pollMs={1000} />

            <MapContainer
                center={[51.505, -0.09]}
                zoom={zoom}
                scrollWheelZoom={true}
                ref={mapRef}
                zoomControl={false}
                style={{ height: '100%', width: '100%' }}
            >
                <ZoomControl position="bottomleft" />
                <TileLayer
                    key={currentMap?.url}
                    attribution={currentMap?.attribution || ''}
                    url={currentMap?.url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                />
                {currentMap?.type === 'rainviewer' && rainViewerData.host && rainViewerData.path && (
                    <TileLayer
                        key={`rainviewer-${rainViewerData.ts}`}
                        url={`${rainViewerData.host}${rainViewerData.path}/256/{z}/{x}/{y}/2/1_1.png`}
                        opacity={0.8}
                        maxNativeZoom={7} // RainViewer coverage ends around z=7/8
                    />
                )}

                {showImageOverlay && config?.overlaysUrl && (
                    <ImageOverlayLayer configUrl={`${process.env.PUBLIC_URL || ''}/${config.overlaysUrl}`} />
                )}

                {/* Load GPX tracks */}
                <GPXLoaderSQL
                    setSectionsData={setSections}
                    selectedSection={selectedSection}
                    selectedStageId={selectedStageId}
                    setOdcinkiData={setOdcinkiData}
                />


                {/* Clustered Markers for devices */}
                <MarkerClusterComponent
                    devicePositions={enrichedPositions}
                    odcinkiData={odcinkiData}
                    setSelectedDevice={setSelectedDevice}
                    modelIcons={modelIcons}
                    selectedDeviceId={selectedDevice?.deviceId ?? selectedDevice?.id ?? null}
                />
                <SpecialMarkers markersFileUrl="/GPX/markers.json" />

                {showSafetyPoints && (
                    <SafetyPointsLayer stageId={selectedStageId} />
                )}

                {isHazardDisplayActive && <HazardMarkers devices={enrichedPositions} />}

                {areaSelectionMode && (
                    <AreaSelectionLayer onAreaSelected={handleAreaSelected} />
                )}

                {/* Watermark in bottom-left corner */}
                <Watermark />
            </MapContainer>

            {areaSelectedDevices && (
                <AreaSelectionPopup
                    devices={areaSelectedDevices}
                    modelColors={modelColors}
                    isReadOnly={isReadOnly}
                    onClose={() => setAreaSelectedDevices(null)}
                    onDeviceClick={(device) => {
                        setAreaSelectedDevices(null);
                        setSelectedDevice({ ...device });
                    }}
                />
            )}

            {/* Live-Updating Device Info Popup */}
            {selectedDevice && (
                <DeviceInfoPopup
                    device={selectedDevice}
                    onClose={() => setSelectedDevice(null)}
                    deviceModels={deviceModels}
                    onModelChange={handleModelUpdate}
                    onConfirmSos={() => handleConfirmSos(selectedDevice.deviceId || selectedDevice.id)}
                    onOpenStatus={(dev) => setStatusModalDevice(dev)}
                    isReadOnly={isReadOnly}
                />
            )}

            {/* Stable Status Modal — hosted at top level and statefully managed to survive refreshes */}
            {statusModalDevice && (
                <DeviceStatusModal
                    device={statusModalDevice}
                    onClose={() => setStatusModalDevice(null)}
                    isReadOnly={isReadOnly}
                />
            )}

            {/* Side panel with compact UI */}
            <div className="side-panel">
                <CompactSidePanel
                    devices={enrichedPositions}
                    stages={odcinkiData}
                    sections={sections}
                    selectedStageId={selectedStageId}
                    selectedStageName={selectedStageName}
                    onDeviceClick={handleDeviceClick}
                    onStageChange={(name) => {
                        const st = odcinkiData.find((s) => s.name === name);
                        setSelectedStageName(name);
                        setSelectedStageId(st ? st.id : null);
                    }}
                    onStageSelect={(st, sec) => {
                        if (selectedSection !== sec.section) {
                            setSelectedSection(sec.section);
                        }
                        setSelectedStageId(st.id);
                        setSelectedStageName(st.name);
                    }}
                    onSectionSelect={(sec) => {
                        setSelectedSection(sec.section);
                        setSelectedStageId(null);
                        setSelectedStageName('');
                    }}
                    selectedSectionName={selectedSection}
                    modelColors={modelColors}
                    modelShowInProgressBar={modelShowInProgressBar}
                    clockOffset={config?.clockOffset || 0}
                    timeControlConfig={config?.timeControl || null}
                    resultsConfig={config}
                    currentMapId={currentMap.id}
                    onMapChange={(id) => {
                        const map = MAP_LAYERS.find(m => m.id === id);
                        if (map) setCurrentMap(map);
                    }}
                    isReadOnly={isReadOnly}
                />
            </div>

        </div>
    );
}

export default MonitoringDashboard;
